import { useState, useEffect } from 'react'
import { ChevronUp, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { leadsAPI } from '../api'
import type { LeadRow, LeadCategory } from '../api'
import { formatNorwegianDate } from '../../../utils/dateUtils'
import LeadsTableExport from '../LeadsTableExport'
import { LEADS_EXPORT_LIMIT } from '../eksportTilgang'
import type { ColumnDefinition } from '../../../utils/exportToCsv'

interface Props {
  orgNr: string
  /** For the export's title (#885). */
  orgNavn?: string | null
}

const PAGE_SIZE = 50
const VEHICLE_CLASS_LABEL: Record<string, 'common.personbil' | 'common.varebil'> = { personbil: 'common.personbil', varebil: 'common.varebil' }

/** sort_by values /leads/leads accepts; sorting is server-side because the
 *  table is server-paginated. */
type SortKey = 'lead_score'

interface LeadsQuery {
  orgNr: string; category: string; vehicleClass: string; sortBy: SortKey; sortDir: 'asc' | 'desc'
}

/** The grid's filters and sort, for one page or for the export (#885). */
function leadsParams(q: LeadsQuery, limit: number, offset: number) {
  return {
    org_nr: q.orgNr,
    category_code: q.category || undefined,
    vehicle_class: q.vehicleClass || undefined,
    sort_by: q.sortBy,
    sort_dir: q.sortDir,
    limit,
    offset,
  }
}

const exportDate = (v: string | null | undefined) => (v ? formatNorwegianDate(v) : '—')

export default function LeadsSection({ orgNr, orgNavn }: Props) {
  const { t } = useTranslation('leads')
  const [leads, setLeads] = useState<LeadRow[]>([])
  const [total, setTotal] = useState(0)
  const [totalCapped, setTotalCapped] = useState(false)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [categories, setCategories] = useState<LeadCategory[]>([])
  const [categoryFilter, setCategoryFilter] = useState('')
  const [vehicleClassFilter, setVehicleClassFilter] = useState('')
  const [sortBy, setSortBy] = useState<SortKey>('lead_score')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  useEffect(() => {
    leadsAPI.getCategories().then(res => setCategories(res.data)).catch(() => {})
  }, [])

  useEffect(() => { setPage(1) }, [categoryFilter, vehicleClassFilter, sortBy, sortDir])

  useEffect(() => {
    setLoading(true)
    leadsAPI.getLeads(leadsParams({
      orgNr, category: categoryFilter, vehicleClass: vehicleClassFilter, sortBy, sortDir,
    }, PAGE_SIZE, (page - 1) * PAGE_SIZE))
      .then(res => {
        setLeads(res.data.items)
        setTotal(res.data.total)
        setTotalCapped(res.data.total_is_capped)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [orgNr, page, categoryFilter, vehicleClassFilter, sortBy, sortDir])

  function handleSort(key: SortKey) {
    if (sortBy === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key)
      setSortDir('desc')
    }
  }

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const exportColumns: ColumnDefinition[] = [
    { key: 'kjennemerke', label: t('common.kjennemerke'), format: v => v ?? '—' },
    { key: 'category_name', label: t('common.kategori') },
    { key: 'lead_score', label: t('common.score') },
    { key: 'merke', label: t('common.merke'), format: v => v ?? '—' },
    { key: 'modell', label: t('common.modell'), format: v => v ?? '—' },
    { key: 'vehicle_class', label: t('common.type'), format: v => (v && VEHICLE_CLASS_LABEL[v] ? t(VEHICLE_CLASS_LABEL[v]) : v ?? '—') },
    { key: 'registrert_forste_gang', label: t('common.firstReg'), format: exportDate },
    { key: 'registrert_forste_gang_pa_eier', label: t('common.regPaEier'), format: exportDate },
  ]

  return (
    <div className="card p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t('common.leads')}</h2>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            className="input-field text-sm"
          >
            <option value="">{t('leadsSection.allCategories')}</option>
            {categories.map(c => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
          <select
            value={vehicleClassFilter}
            onChange={e => setVehicleClassFilter(e.target.value)}
            className="input-field text-sm"
          >
            <option value="">{t('common.allTypes')}</option>
            <option value="personbil">{t('common.personbil')}</option>
            <option value="varebil">{t('common.varebil')}</option>
          </select>
          <span className="text-sm text-gray-500 dark:text-gray-400">{t('common.totalCount', { n: total })}</span>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" />
      ) : leads.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">{t('leadsSection.empty')}</p>
      ) : (
        <>
          <LeadsTableExport
            rows={leads}
            columns={exportColumns}
            title={t('tableExport.orgTitle', { table: t('common.leads'), navn: orgNavn ?? orgNr, orgNr })}
            loadData={() => leadsAPI.getLeads(leadsParams({
              orgNr, category: categoryFilter, vehicleClass: vehicleClassFilter, sortBy, sortDir,
            }, LEADS_EXPORT_LIMIT, 0)).then(res => res.data.items)}
            capped={totalCapped}
            testId="bedrift-leads-export"
          />
          <div className="overflow-x-auto">
            <table className="data-table data-table-compact">
              <thead>
                <tr>
                  <th className="text-left whitespace-nowrap">{t('common.kjennemerke')}</th>
                  <th className="text-left whitespace-nowrap">{t('common.kategori')}</th>
                  <SortHeader label={t('common.score')} sortKey="lead_score" current={sortBy} dir={sortDir} onSort={handleSort} align="right" />
                  <th className="text-left whitespace-nowrap">{t('common.merke')}</th>
                  <th className="text-left whitespace-nowrap">{t('common.modell')}</th>
                  <th className="text-left whitespace-nowrap">{t('common.type')}</th>
                  <th className="text-left whitespace-nowrap">{t('common.firstReg')}</th>
                  <th className="text-left whitespace-nowrap">{t('common.regPaEier')}</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((row, idx) => (
                  <tr key={row.id} className={idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}>
                    <td className="font-mono text-xs text-primary-600 dark:text-primary-400">{row.kjennemerke ?? '—'}</td>
                    <td>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
                        {row.category_name}
                      </span>
                    </td>
                    <td className="text-right font-bold">{row.lead_score}</td>
                    <td className="text-sm">{row.merke ?? '—'}</td>
                    <td className="text-sm">{row.modell ?? '—'}</td>
                    <td className="text-xs">{row.vehicle_class ? (VEHICLE_CLASS_LABEL[row.vehicle_class] ? t(VEHICLE_CLASS_LABEL[row.vehicle_class]) : row.vehicle_class) : '—'}</td>
                    <td className="text-xs text-gray-500">{row.registrert_forste_gang ? formatNorwegianDate(row.registrert_forste_gang) : '—'}</td>
                    <td className="text-xs font-medium">{row.registrert_forste_gang_pa_eier ? formatNorwegianDate(row.registrert_forste_gang_pa_eier) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mt-4">
              <span>{t('common.pageOf', { page, pages })}</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  {t('common.prev')}
                </button>
                <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
                  className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  {t('common.next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function SortHeader({ label, sortKey, current, dir, onSort, align }: {
  label: string
  sortKey: SortKey
  current: SortKey
  dir: 'asc' | 'desc'
  onSort: (key: SortKey) => void
  align?: 'right'
}) {
  const active = current === sortKey
  return (
    <th
      className={`${align === 'right' ? 'text-right' : 'text-left'} whitespace-nowrap cursor-pointer select-none hover:text-primary-600 dark:hover:text-primary-400 transition-colors`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          dir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        ) : (
          <span className="w-3 h-3 inline-block" />
        )}
      </span>
    </th>
  )
}
